import { COUNT_PER_PAGE } from '../../constants/Constants';
import React, { useEffect, useMemo, useState } from 'react';
import { useRecoilValue, useRecoilValueLoadable } from 'recoil';
import { CodeUtil } from '../../utils/CodeUtil';
import { Column } from 'primereact/column';
import { CommonUtil } from '../../utils/commonUtil';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { MyInfoUtil } from '../../utils/myInfoUtil';
import { ProgressBar } from 'primereact/progressbar';
import { RadioButton } from 'primereact/radiobutton';
import { Button as PrimeButton } from 'primereact/button';
import RangeCalendar from '../../components/Common/RangeCalendar';
import SmsUnitPriceDialog from '../../components/Dialog/SmsUnitPriceDialog';
import SearchMenu from '../../components/Menu/SearchMenu';
import { ServiceProvider } from '../../services';
import { Tag } from 'primereact/tag';
import _ from 'lodash';
import { classNames } from 'primereact/utils';
import dayjs from 'dayjs';
import { myInfoSelector } from '../../recoil/selectors';
import { touchUIState } from '../../recoil/atoms';
import { useAuthorizedFetch } from '../../hooks/useAuthorizedFetch';

const smsSettlementService = ServiceProvider.smsSettlement;

const today = dayjs();

const DATE_PRESET_OPTIONS = [
  { value: 'thisMonth', label: '당월' },
  { value: 'lastMonth', label: '전월' },
  { value: 'oneYear', label: '1년' },
];

const SETTLEMENT_STATUS_OPTIONS = [
  { value: 'UNSETTLED', label: '미정산' },
  { value: 'SETTLED', label: '정산완료' },
  { value: 'UNPAID', label: '미납' },
];

const getDateRangeByPreset = (preset) => {
  const now = dayjs();

  switch (preset) {
    case 'thisMonth':
      return {
        startDate: now.startOf('month').toDate(),
        endDate: now.toDate(),
      };
    case 'lastMonth': {
      const prevMonth = now.subtract(1, 'month');
      return {
        startDate: prevMonth.startOf('month').toDate(),
        endDate: prevMonth.endOf('month').toDate(),
      };
    }
    case 'oneYear':
      return {
        startDate: now.subtract(1, 'year').toDate(),
        endDate: now.toDate(),
      };
    default:
      return null;
  }
};

const DEFAULT_SEARCH_CONDITIONS = {
  associations: [],
  enterprises: [],
  shops: [],
  startDate: today.startOf('month').toDate(),
  endDate: today.toDate(),
  settlementStatus: '',
};

const DEFAULT_LAZY_PARAMS = {
  first: 0,
  rows: 10,
  page: 0,
};

const priceFormatter = new Intl.NumberFormat('ko-KR');

const DEFAULT_UNIT_PRICES = {
  sms: 15,
  lms: 35,
  mms: 60,
};

const SmsSettlementList = () => {
  const touchUI = useRecoilValue(touchUIState);
  const myInfoLoadable = useRecoilValueLoadable(myInfoSelector);
  const { myAssociationId, myEnterpriseId } = useMemo(
    () => MyInfoUtil.unpack(myInfoLoadable.contents),
    [myInfoLoadable.contents]
  );

  const [searchConditions, setSearchConditions] = useState({
    ...DEFAULT_SEARCH_CONDITIONS,
  });
  const [datePreset, setDatePreset] = useState('thisMonth');
  const [lazyParams, setLazyParams] = useState({ ...DEFAULT_LAZY_PARAMS });
  const [codes, setCodes] = useState({});
  const [updatingShopId, setUpdatingShopId] = useState(null);
  const [priceDialogVisible, setPriceDialogVisible] = useState(false);

  const {
    loading,
    totalRecords,
    data,
    summary,
    getList,
    getSummary,
    initData,
  } = useAuthorizedFetch(
    smsSettlementService,
    searchConditions,
    setSearchConditions,
    DEFAULT_SEARCH_CONDITIONS,
    lazyParams.page,
    lazyParams.rows,
    true
  );

  const handleDatePresetChange = (preset) => {
    const range = getDateRangeByPreset(preset);
    if (!range) return;

    setDatePreset(preset);
    setSearchConditions((ps) => ({
      ...ps,
      startDate: range.startDate,
      endDate: range.endDate,
    }));
  };

  function handleSelectAll(
    selected,
    field,
    updateCodes,
    updateSearchConditions
  ) {
    CommonUtil.Func.handleSelectAll(
      selected,
      field,
      updateCodes,
      updateSearchConditions,
      codes,
      setCodes,
      setSearchConditions
    );
  }

  const period = dayjs(searchConditions.startDate).format('YYYYMM');

  const handleStatusChange = async (row, status) => {
    setUpdatingShopId(row.shopId);
    try {
      await smsSettlementService.updateStatus({
        shopId: row.shopId,
        period: row.period || period,
        status,
      });
      window.cerp.toast.success('정산 상태 변경', '정산 상태를 저장했습니다.');
      await Promise.all([
        getList(searchConditions, lazyParams.page, lazyParams.rows),
        getSummary(searchConditions),
      ]);
    } catch (error) {
      window.cerp.dialog.error(
        '정산 상태 변경 실패',
        error?.code
          ? `[${error.code}] ${error.message}`
          : '[서버 에러] 시스템 관리자에게 문의하세요.'
      );
    } finally {
      setUpdatingShopId(null);
    }
  };

  useEffect(() => {
    (async () => {
      if (myInfoLoadable.state === 'hasValue') {
        const myInfo = MyInfoUtil.unpack(myInfoLoadable.contents);

        const [associations, associationId] =
          await CodeUtil.getAssociationCodes(myInfo, {
            preSelectedAssociationId: myAssociationId,
          });
        const [enterprises, enterpriseId] = await CodeUtil.getEnterpriseCodes(
          myInfo,
          {
            isParentEmpty: _.isEmpty(associations),
            associationId: myAssociationId,
          }
        );
        const [shops, shopId] = await CodeUtil.getShopCodes(myInfo, {
          isParentEmpty: _.isEmpty(enterprises),
          enterpriseId: myEnterpriseId,
        });

        setCodes((ps) => ({ ...ps, associations, enterprises, shops }));

        await Promise.all([
          getList(
            {
              ...searchConditions,
              associations: [associationId],
              enterprises: [enterpriseId],
              shops: [shopId],
            },
            _.get(DEFAULT_LAZY_PARAMS, 'page'),
            _.get(DEFAULT_LAZY_PARAMS, 'rows')
          ),
          getSummary({
            ...searchConditions,
            associations: [associationId],
            enterprises: [enterpriseId],
            shops: [shopId],
          }),
        ]);
      }
    })();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myInfoLoadable.state]);

  const kpiCards = [
    {
      label: '총 발송 건수',
      value: `${priceFormatter.format(summary?.totalSendCount || 0)} 건`,
      valueClass: 'text-900',
    },
    {
      label: '총 청구 금액',
      value: `₩ ${priceFormatter.format(summary?.totalBillingAmount || 0)}`,
      valueClass: 'text-indigo-600',
    },
    {
      label: '이용 업체 수',
      value: `${priceFormatter.format(summary?.companyCount || 0)} 개사`,
      valueClass: 'text-900',
    },
    {
      label: '미납/미정산 금액',
      value: `₩ ${priceFormatter.format(summary?.unpaidAmount || 0)}`,
      valueClass: 'text-orange-500',
    },
  ];

  return (
    <div className="grid">
      <div className="col-12">
        {(() => {
          switch (myInfoLoadable.state) {
            case 'hasValue': {
              const myInfo = MyInfoUtil.unpack(myInfoLoadable.contents);
              return (
                <>
                  <div className="card mb-3">
                    <div className="flex align-items-center justify-content-between flex-wrap gap-2">
                      <div className="flex align-items-center gap-2">
                        <div className="text-xl font-bold">
                          업체별 문자 발송/정산 관리
                        </div>
                        <PrimeButton
                          icon="pi pi-cog"
                          label="설정"
                          className="p-button-outlined p-button-sm"
                          onClick={() => setPriceDialogVisible(true)}
                        />
                      </div>
                      <Tag
                        value="SMS Gateway Normal"
                        className="bg-green-600"
                      />
                    </div>
                  </div>
                  <SmsUnitPriceDialog
                    visible={priceDialogVisible}
                    initialPrices={{
                      sms: summary?.smsUnitPrice ?? DEFAULT_UNIT_PRICES.sms,
                      lms: summary?.lmsUnitPrice ?? DEFAULT_UNIT_PRICES.lms,
                      mms: summary?.mmsUnitPrice ?? DEFAULT_UNIT_PRICES.mms,
                    }}
                    onHide={() => setPriceDialogVisible(false)}
                    onSaved={() =>
                      Promise.all([
                        getList(
                          searchConditions,
                          lazyParams.page,
                          lazyParams.rows
                        ),
                        getSummary(searchConditions),
                      ])
                    }
                  />

                  <SearchMenu
                    onInit={() => {
                      setDatePreset('thisMonth');
                      setLazyParams({ ...DEFAULT_LAZY_PARAMS });
                      initData();
                    }}
                    onSearch={() => {
                      setLazyParams({ ...DEFAULT_LAZY_PARAMS });
                      Promise.all([
                        getList(
                          searchConditions,
                          _.get(DEFAULT_LAZY_PARAMS, 'page'),
                          _.get(DEFAULT_LAZY_PARAMS, 'rows')
                        ),
                        getSummary(searchConditions),
                      ]);
                    }}
                  >
                    <div className="field col-12 sm:col-6 lg:col-3">
                      <label htmlFor="range">소속협회</label>
                      <MultiSelect
                        className="w-full"
                        options={codes.associations}
                        display="chip"
                        placeholder="전체 협회"
                        value={searchConditions.associations}
                        filter
                        showSelectAll={false}
                        onChange={async ({ value }) => {
                          const [enterprises] =
                            await CodeUtil.getEnterpriseCodes(myInfo, {
                              associationId: value,
                            });
                          const [shops] = await CodeUtil.getShopCodes(myInfo, {
                            isParentEmpty: _.isEmpty(enterprises),
                          });
                          handleSelectAll(
                            value,
                            'associations',
                            { enterprises, shops },
                            { enterprises: [], shops: [] }
                          );
                        }}
                      />
                    </div>
                    <div className="field col-12 sm:col-6 lg:col-3">
                      <label htmlFor="range">소속업체</label>
                      <MultiSelect
                        className="w-full"
                        options={codes.enterprises}
                        display="chip"
                        placeholder="전체 업체"
                        value={searchConditions.enterprises}
                        filter
                        showSelectAll={false}
                        onChange={async ({ value }) => {
                          const [shops] = await CodeUtil.getShopCodes(myInfo, {
                            isParentEmpty: _.isEmpty(value),
                            enterpriseId: value,
                          });
                          handleSelectAll(
                            value,
                            'enterprises',
                            { shops },
                            { shops: [] }
                          );
                        }}
                      />
                    </div>
                    <div className="field col-12 sm:col-6 lg:col-3">
                      <label htmlFor="range">소속점검장</label>
                      <MultiSelect
                        className="w-full"
                        options={codes.shops}
                        display="chip"
                        placeholder="전체 점검장"
                        value={searchConditions.shops}
                        filter
                        showSelectAll={false}
                        onChange={({ value }) => {
                          handleSelectAll(value, 'shops');
                        }}
                      />
                    </div>
                    <div className="field col-12 sm:col-6 lg:col-3">
                      <label htmlFor="range">정산 상태</label>
                      <div className="flex flex-auto flex-wrap align-items-center justify-content-start mt-2 gap-3">
                        <div className="flex align-items-center gap-1">
                          <RadioButton
                            inputId="radio_settle_all"
                            name="settlementStatus"
                            value=""
                            onChange={({ value }) => {
                              setSearchConditions((ps) => ({
                                ...ps,
                                settlementStatus: value,
                              }));
                            }}
                            checked={searchConditions.settlementStatus === ''}
                          />
                          <label htmlFor="radio_settle_all">전체</label>
                        </div>
                        {SETTLEMENT_STATUS_OPTIONS.map((option) => (
                          <div
                            key={option.value}
                            className="flex align-items-center gap-1"
                          >
                            <RadioButton
                              inputId={`radio_settle_${option.value}`}
                              name="settlementStatus"
                              value={option.value}
                              onChange={({ value }) => {
                                setSearchConditions((ps) => ({
                                  ...ps,
                                  settlementStatus: value,
                                }));
                              }}
                              checked={
                                searchConditions.settlementStatus ===
                                option.value
                              }
                            />
                            <label htmlFor={`radio_settle_${option.value}`}>
                              {option.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="field col-12">
                      <label htmlFor="range">발송일자</label>
                      <div className="flex align-items-center flex-wrap gap-2">
                        <RangeCalendar
                          startDate={searchConditions.startDate}
                          endDate={searchConditions.endDate}
                          onStartDateChanged={(date) => {
                            setDatePreset(null);
                            setSearchConditions((ps) => ({
                              ...ps,
                              startDate: date,
                            }));
                          }}
                          onEndDateChanged={(date) => {
                            setDatePreset(null);
                            setSearchConditions((ps) => ({
                              ...ps,
                              endDate: date,
                            }));
                          }}
                          touchUI={touchUI}
                          showNavigators={true}
                        />
                        <div className="p-buttonset flex-shrink-0">
                          {DATE_PRESET_OPTIONS.map((option) => (
                            <PrimeButton
                              key={option.value}
                              label={option.label}
                              className={classNames('p-button-sm', {
                                'p-button-outlined':
                                  datePreset !== option.value,
                              })}
                              onClick={() =>
                                handleDatePresetChange(option.value)
                              }
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </SearchMenu>

                  <div className="grid">
                    {kpiCards.map((card) => (
                      <div key={card.label} className="col-12 md:col-6 xl:col-3">
                        <div className="card mb-0">
                          <span className="text-sm text-600 font-semibold">
                            {card.label}
                          </span>
                          <div
                            className={classNames(
                              'text-2xl font-bold mt-2',
                              card.valueClass
                            )}
                          >
                            {card.value}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="card">
                    <DataTable
                      loading={loading}
                      value={data}
                      lazy
                      rows={lazyParams.rows}
                      first={lazyParams.first}
                      totalRecords={totalRecords}
                      paginator
                      rowsPerPageOptions={COUNT_PER_PAGE}
                      onPage={(e) => {
                        setLazyParams(e);
                        getList(searchConditions, e.page, e.rows);
                      }}
                      responsiveLayout="scroll"
                      emptyMessage="데이터가 없습니다."
                      rowHover
                    >
                      <Column
                        header="No."
                        className="text-center"
                        style={{ width: '4.5rem' }}
                        body={(_row, { rowIndex }) =>
                          lazyParams.first + rowIndex + 1
                        }
                      />
                      <Column
                        header="업체명 (사업자번호)"
                        body={({ shopName, businessRegistrationNumber }) => (
                          <span>
                            {shopName}
                            {businessRegistrationNumber
                              ? ` (${businessRegistrationNumber})`
                              : ''}
                          </span>
                        )}
                      />
                      <Column
                        field="smsCount"
                        header={`SMS(${summary?.smsUnitPrice || 15}원)`}
                        className="text-right"
                        body={({ smsCount }) =>
                          priceFormatter.format(smsCount || 0)
                        }
                      />
                      <Column
                        field="lmsCount"
                        header={`LMS(${summary?.lmsUnitPrice || 35}원)`}
                        className="text-right"
                        body={({ lmsCount }) =>
                          priceFormatter.format(lmsCount || 0)
                        }
                      />
                      <Column
                        field="mmsCount"
                        header={`MMS(${summary?.mmsUnitPrice || 60}원)`}
                        className="text-right"
                        body={({ mmsCount }) =>
                          priceFormatter.format(mmsCount || 0)
                        }
                      />
                      <Column
                        field="totalCount"
                        header="총 건수"
                        className="text-right font-bold"
                        body={({ totalCount }) =>
                          priceFormatter.format(totalCount || 0)
                        }
                      />
                      <Column
                        field="billingAmount"
                        header="청구 금액(원)"
                        className="text-right font-bold text-indigo-800"
                        body={({ billingAmount }) =>
                          priceFormatter.format(billingAmount || 0)
                        }
                      />
                      <Column
                        header="정산 상태"
                        className="text-center"
                        style={{ width: '10rem' }}
                        body={(row) => {
                          const status = row.settlementStatus || 'UNSETTLED';
                          return (
                            <Dropdown
                              className="w-full"
                              options={SETTLEMENT_STATUS_OPTIONS}
                              value={status}
                              disabled={updatingShopId === row.shopId}
                              onChange={({ value }) =>
                                handleStatusChange(row, value)
                              }
                            />
                          );
                        }}
                      />
                    </DataTable>
                  </div>
                </>
              );
            }
            case 'loading':
              return <ProgressBar mode="indeterminate" />;
            case 'hasError':
              return null;
            default:
          }
        })()}
      </div>
    </div>
  );
};

const comparisonFn = function (prevProps, nextProps) {
  return prevProps.location.pathname === nextProps.location.pathname;
};

export default React.memo(SmsSettlementList, comparisonFn);
